import { createContext, useContext } from "react";

import { BoostedPostSeparatorBaseConfig } from "@ui/components/article/BoostedPostSeparator/BoostedPostSeparator";
import { HighlightBaseConfig } from "@ui/components/article/Highlight/Highlight";
import { ProductCardBaseConfig } from "@ui/components/article/ProductCard/ProductCard";
import { ShareButtonsBaseConfig } from "@ui/components/article/ShareButtons/ShareButtons";
import { NotificationSettingsBaseConfig } from "@ui/components/auth/account/NotificationSettings/NotificationSettings";
import { CheckoutConfirmationBaseConfig } from "@ui/components/checkout/CheckoutConfirmation";
import { SubscriptionCollageBaseConfig } from "@ui/components/checkout/SubscriptionCollage";
import { CompactContentCardBaseConfig } from "@ui/components/content-cards/CompactContentCard";
import { ContentBlockBylineBaseConfig } from "@ui/components/content-cards/ContentBlock/components/ContentBlockByline/ContentBlockByline";
import { ContentBlockCrossCommunityPostBaseConfig } from "@ui/components/content-cards/ContentBlock/components/ContentBlockCrossCommunityPost/ContentBlockCrossCommunityPost";
import { ContentBlockMetaBaseConfig } from "@ui/components/content-cards/ContentBlock/components/ContentBlockMeta";
import { ContentCardScoreBaseConfig } from "@ui/components/content-cards/ContentCardScore";
import { ProminentContentCardBaseConfig } from "@ui/components/content-cards/ProminentContentCard";
import { QuickPostBaseConfig } from "@ui/components/content-cards/QuickPost";
import { SplitContentCardBaseConfig } from "@ui/components/content-cards/SplitContentCard";
import { StreamEntryBylineBaseConfig } from "@ui/components/content-cards/StreamEntry/components/StreamEntryByline/StreamEntryByline";
import { ContactFormBaseConfig } from "@ui/components/cta/ContactForm";
import { NewsletterBaseConfig } from "@ui/components/cta/Newsletter";
import { NewslettersPageCardBaseConfig } from "@ui/components/cta/NewslettersPageCard";
import { HeaderPatternBaseConfig } from "@ui/components/layout/HeaderPattern";
import { PageHeaderBaseConfig } from "@ui/components/layout/PageHeader";
import { PatternSwitcherBaseConfig } from "@ui/components/layout/PatternSwitcher";
import { RiverBaseConfig } from "@ui/components/layout/River";
import { UserProfileContentBaseConfig } from "@ui/components/layout/UserProfileContent";
import { DisclaimerBaseConfig } from "@ui/components/ledes/Disclaimer";
import { FeatureFullWidthLedeBaseConfig } from "@ui/components/ledes/FeatureFullWidthLede";
import { FeatureSquareLedeBaseConfig } from "@ui/components/ledes/FeatureSquareLede";
import { LedeBylineBaseConfig } from "@ui/components/ledes/LedeByline";
import { LedeMetaBaseConfig } from "@ui/components/ledes/LedeMeta";
import { SpecialFeatureFullWidthLedeBaseConfig } from "@ui/components/ledes/SpecialFeatureFullWidthLede";
import { SpecialFeatureSquareLedeBaseConfig } from "@ui/components/ledes/SpecialFeatureSquareLede";
import { StandardLedeBaseConfig } from "@ui/components/ledes/StandardLede";
import { EmbedBaseConfig } from "@ui/components/media/Embed";
import { ImageSliderBaseConfig } from "@ui/components/media/ImageSlider";
import { LedeImageBaseConfig } from "@ui/components/media/LedeImage";
import { VideoEmbedBaseConfig } from "@ui/components/media/VideoEmbed";
import { MetaCategoryBaseConfig } from "@ui/components/meta/Category";
import { MetaCommentCountBaseConfig } from "@ui/components/meta/CommentCount";
import { MetaTimestampBaseConfig } from "@ui/components/meta/Timestamp/Timestamp";
import { FooterBaseConfig } from "@ui/components/navigation/Footer";
import { NavigationBaseConfig } from "@ui/components/navigation/Navigation";
import { SocialLinksBaseConfig } from "@ui/components/navigation/SocialLinks";
import { NotificationsListBaseConfig } from "@ui/components/notifications/NotificationsList/NotificationsList";
import { FiveGridOverflowPatternBaseConfig } from "@ui/components/patterns/FiveGridOverflowPattern/FiveGridOverflowPattern";
import { FourUpAltPatternBaseConfig } from "@ui/components/patterns/FourUpAltPattern/FourUpAltPattern";
import { FourUpPatternBaseConfig } from "@ui/components/patterns/FourUpPattern/FourUpPattern";
import { Pattern } from "@ui/components/patterns/patterns.types";
import { ArticleParentRecircBaseConfig } from "@ui/components/recirculation/ArticleParentRecirc/ArticleParentRecirc";
import { ArticleRecircBaseConfig } from "@ui/components/recirculation/ArticleRecirc/ArticleRecirc";
import { MostPopularListBaseConfig } from "@ui/components/recirculation/MostPopularList";
import { mergeProps } from "@ui/utils/merge-props";

/**
 * Component context configs are key value pairs where the key is the context
 * in which the component appears (e.g. "hubLayout") and the value is a partial
 * of the base config. This allows for overriding of the base config in specific
 * contexts (layouts, patterns, etc.)
 *
 * ComponentContextConfig is a type function that takes a base config and creates a
 * componentContext type where the key is the componentContext string and the value
 * is a partial of the base config.
 */
type ComponentContextConfig<BaseConfig> = {
  [componentContext: string]: Partial<BaseConfig>;
};
/**
 * OvertoneConfig is a type function that takes a base config and creates
 * a type component config which is the BaseConfig with a componentContext property
 * where the value of the componentContext property is a partial of the base config.
 *
 * Since brand specific config is merged with the Overtone config, no keys are required.
 * To type brand configs a partial is used to allow for the omission of key value pairs
 * from the base (overtone) component config. E.g. Partial<CompactContentCardConfig>
 */
type OvertoneConfig<BaseConfig> = BaseConfig & {
  componentContext?: ComponentContextConfig<BaseConfig>;
};

export type BoostedPostSeparatorConfig =
  OvertoneConfig<BoostedPostSeparatorBaseConfig>;
export type ContactFormConfig = OvertoneConfig<ContactFormBaseConfig>;
export type ContentBlockMetaConfig = OvertoneConfig<ContentBlockMetaBaseConfig>;
export type ContentBlockCrossCommunityPostConfig =
  OvertoneConfig<ContentBlockCrossCommunityPostBaseConfig>;
export type ProminentContentCardConfig =
  OvertoneConfig<ProminentContentCardBaseConfig>;
export type SplitContentCardConfig = OvertoneConfig<SplitContentCardBaseConfig>;
export type CompactContentCardConfig =
  OvertoneConfig<CompactContentCardBaseConfig>;
export type FooterConfig = OvertoneConfig<FooterBaseConfig>;
export type HeaderPatternConfig = OvertoneConfig<HeaderPatternBaseConfig>;
export type HighlightConfig = OvertoneConfig<HighlightBaseConfig>;
export type MetaCategoryConfig = OvertoneConfig<MetaCategoryBaseConfig>;
export type MetaCommentCountConfig = OvertoneConfig<MetaCommentCountBaseConfig>;
export type MetaTimestampConfig = OvertoneConfig<MetaTimestampBaseConfig>;
export type NavigationConfig = OvertoneConfig<NavigationBaseConfig>;
export type FeatureFullWidthLedeConfig =
  OvertoneConfig<FeatureFullWidthLedeBaseConfig>;
export type FeatureSquareLedeConfig =
  OvertoneConfig<FeatureSquareLedeBaseConfig>;
export type SpecialFeatureFullWidthLedeConfig =
  OvertoneConfig<SpecialFeatureFullWidthLedeBaseConfig>;
export type SpecialFeatureSquareLedeConfig =
  OvertoneConfig<SpecialFeatureSquareLedeBaseConfig>;
export type StandardLedeConfig = OvertoneConfig<StandardLedeBaseConfig>;
export type PatternSwitcherConfig = OvertoneConfig<PatternSwitcherBaseConfig>;
export type SocialLinksConfig = OvertoneConfig<SocialLinksBaseConfig>;
export type PageHeaderConfig = OvertoneConfig<PageHeaderBaseConfig>;
export type RiverConfig = OvertoneConfig<RiverBaseConfig>;
export type DisclaimerConfig = OvertoneConfig<DisclaimerBaseConfig>;
export type LedeBylineConfig = OvertoneConfig<LedeBylineBaseConfig>;
export type LedeImageConfig = OvertoneConfig<LedeImageBaseConfig>;
export type LedeMetaConfig = OvertoneConfig<LedeMetaBaseConfig>;
export type VideoEmbedConfig = OvertoneConfig<VideoEmbedBaseConfig>;
export type EmbedConfig = OvertoneConfig<EmbedBaseConfig>;
export type ImageSliderConfig = OvertoneConfig<ImageSliderBaseConfig>;
export type FourUpAltPatternConfig = OvertoneConfig<FourUpAltPatternBaseConfig>;
export type FourUpPatternConfig = OvertoneConfig<FourUpPatternBaseConfig>;
export type FiveGridOverflowPatternConfig =
  OvertoneConfig<FiveGridOverflowPatternBaseConfig>;
export type ThreeGridOverflowPatternConfig = OvertoneConfig<Pattern>;
export type ThreeGridAltOverflowPatternConfig = OvertoneConfig<Pattern>;
export type ThreeGridBigPatternConfig = OvertoneConfig<Pattern>;
export type ThreeUpPatternConfig = OvertoneConfig<Pattern>;
export type ShareButtonsConfig = OvertoneConfig<ShareButtonsBaseConfig>;
export type CheckoutConfirmationConfig =
  OvertoneConfig<CheckoutConfirmationBaseConfig>;
export type ArticleRecircConfig = OvertoneConfig<ArticleRecircBaseConfig>;
export type ArticleParentRecircConfig =
  OvertoneConfig<ArticleParentRecircBaseConfig>;
export type QuickPostConfig = OvertoneConfig<QuickPostBaseConfig>;
export type ContentCardScoreConfig = OvertoneConfig<ContentCardScoreBaseConfig>;
export type ContentBlockBylineConfig =
  OvertoneConfig<ContentBlockBylineBaseConfig>;
export type MostPopularListConfig = OvertoneConfig<MostPopularListBaseConfig>;
export type ProductCardConfig = OvertoneConfig<ProductCardBaseConfig>;
export type NewsletterConfig = OvertoneConfig<NewsletterBaseConfig>;
export type NewslettersPageCardConfig =
  OvertoneConfig<NewslettersPageCardBaseConfig>;
export type NotificationsListConfig =
  OvertoneConfig<NotificationsListBaseConfig>;
export type NotificationSettingsConfig =
  OvertoneConfig<NotificationSettingsBaseConfig>;
export type StreamEntryBylineConfig =
  OvertoneConfig<StreamEntryBylineBaseConfig>;
export type SubscriptionCollageConfig =
  OvertoneConfig<SubscriptionCollageBaseConfig>;
export type UserProfileContentConfig =
  OvertoneConfig<UserProfileContentBaseConfig>;

export type ComponentConfigs = {
  boostedPostSeparator: BoostedPostSeparatorConfig;
  contactForm: ContactFormConfig;
  contentBlockMeta: ContentBlockMetaConfig;
  contentBlockCrossCommunityPost: ContentBlockCrossCommunityPostConfig;
  contentCardScore: ContentCardScoreConfig;
  prominentContentCard: ProminentContentCardConfig;
  splitContentCard: SplitContentCardConfig;
  compactContentCard: CompactContentCardConfig;
  footer: FooterConfig;
  headerPattern: HeaderPatternConfig;
  highlight: HighlightConfig;
  featureFullWidthLede: FeatureFullWidthLedeConfig;
  featureSquareLede: FeatureSquareLedeConfig;
  specialFeatureFullWidthLede: SpecialFeatureFullWidthLedeConfig;
  specialFeatureSquareLede: SpecialFeatureSquareLedeConfig;
  patternSwitcher: PatternSwitcherConfig;
  standardLede: StandardLedeConfig;
  metaCategory: MetaCategoryConfig;
  metaCommentCount: MetaCommentCountConfig;
  metaTimestamp: MetaTimestampConfig;
  navigation: NavigationConfig;
  notificationsList: NotificationsListConfig;
  notificationSettings: NotificationSettingsConfig;
  pageHeader: PageHeaderConfig;
  river: RiverConfig;
  disclaimer: DisclaimerConfig;
  socialLinks: SocialLinksConfig;
  ledeByline: LedeBylineConfig;
  ledeImage: LedeImageConfig;
  ledeMeta: LedeMetaConfig;
  videoEmbed: VideoEmbedConfig;
  embed: EmbedConfig;
  imageSlider: ImageSliderConfig;
  fourUpAltPattern: FourUpAltPatternConfig;
  fourUpPattern: FourUpPatternConfig;
  fiveGridOverflowPattern: FiveGridOverflowPatternConfig;
  threeGridAltOverflowPattern: ThreeGridAltOverflowPatternConfig;
  threeGridBigPattern: ThreeGridBigPatternConfig;
  threeGridOverflowPattern: ThreeGridOverflowPatternConfig;
  threeUpPattern: ThreeUpPatternConfig;
  shareButtons: ShareButtonsConfig;
  checkoutConfirmation: CheckoutConfirmationConfig;
  articleRecirc: ArticleRecircConfig;
  articleParentRecirc: ArticleParentRecircConfig;
  mostPopularList: MostPopularListConfig;
  quickPost: QuickPostConfig;
  contentBlockByline: ContentBlockBylineConfig;
  productCard: ProductCardConfig;
  newsletter: NewsletterConfig;
  newslettersPageCard: NewslettersPageCardConfig;
  streamEntryByline: StreamEntryBylineConfig;
  subscriptionCollage: SubscriptionCollageConfig;
  userProfileContent: UserProfileContentConfig;
};

type Component = keyof ComponentConfigs;

const ComponentConfigContext = createContext<ComponentConfigs | null>(null);

export const useComponentConfig = <C extends Component>(
  component: C,
  componentContext = "",
): ComponentConfigs[C] => {
  const componentConfigContext = useContext(ComponentConfigContext);

  if (!componentConfigContext) {
    throw new Error("Missing ComponentConfig Context");
  }

  const componentConfig = componentConfigContext[component];

  const { componentContext: componentContextConfigs, ...baseConfig } =
    componentConfig;

  /**
   * If the component config has configurations for specific contexts, get
   * the configuration for the supplied component context should it exist
   * and merge it with the base config. Otherwise, return the base config.
   */
  const config = componentContextConfigs?.[componentContext]
    ? mergeProps(baseConfig, componentContextConfigs[componentContext])
    : baseConfig;

  return config as ComponentConfigs[C];
};

/**
 * Combines multiple component contexts into a single string camel cased string
 */
export const combineComponentContexts = (componentContexts: string[]) => {
  const [firstContext, ...remainingContexts] = componentContexts;

  return [
    firstContext,
    ...remainingContexts.map(
      (context: string) =>
        `${context.charAt(0).toUpperCase()}${context.slice(1)}`,
    ),
  ].join("");
};

export default ComponentConfigContext;
